import {ZIDX, ZIDXPage} from "../../../standalone/standaloneLibrary.js";
import {peakAlert, peakConfirm} from "../../../PeakDialog.js";

/**
 * Site AI Policy and Chatbot Instructions page. The server renders both documents read-only; this class builds the
 * Refine with AI panel (chat, proposal with diff, Accept / Refine / Cancel) and the version history with Revert.
 * The browser never sends document text to the server: it sends the session token and the hash of the proposal shown.
 * All text from the server is inserted with textContent.
 */
export class ZIDXAccountAIPolicy implements ZIDXPage {
    zidx: ZIDX;

    constructor(zidx: ZIDX) {
        this.zidx = zidx;
    }

    getPageClass() {
        return "zidxAccountAIPolicyContainer";
    }

    private root(): ParentNode {
        return this.zidx.peakidxShadowRoot.querySelector(".zidxAccountAIPolicyContainer.zidxPageActive") || this.zidx.peakidxShadowRoot;
    }

    render() {
        const root = this.root();
        root.querySelectorAll<HTMLElement>(".zidxAiPolicyRefine").forEach((b) => {
            b.onclick = () => this.openPanel(b.getAttribute("data-target") || "");
        });
        root.querySelectorAll<HTMLElement>(".zidxAiPolicyHistory").forEach((b) => {
            b.onclick = () => this.openHistory(b.getAttribute("data-target") || "");
        });
    }

    private host(target: string): HTMLElement | null {
        return this.root().querySelector<HTMLElement>('.zidxAiPolicyHost[data-target="' + target + '"]');
    }

    private post(url: string, data: any, ok: (d: any) => void, fail?: () => void) {
        this.zidx.ajaxRequest({
            id: "aiPolicy" + url,
            method: "post",
            url: url,
            postObj: data,
            callback: (r: string) => {
                let rs: any;
                try {
                    rs = JSON.parse(r);
                } catch (e) {
                    peakAlert("Unexpected response from the server.");
                    if (fail) fail();
                    return;
                }
                if (!rs.success) {
                    peakAlert(rs.errorMessage || "Request failed.");
                    if (fail) fail();
                    return;
                }
                let d: any = null;
                if (rs.data) {
                    try {
                        d = JSON.parse(rs.data);
                    } catch (e) {
                        d = null;
                    }
                }
                ok(d || rs);
            },
            errorCallback: () => {
                peakAlert("There was a temporary server or network problem, please try again later.");
                if (fail) fail();
            }
        });
    }

    private el(tag: string, text?: string, style?: string): HTMLElement {
        const e = document.createElement(tag);
        if (text !== undefined) e.textContent = text;
        if (style) e.setAttribute("style", style);
        return e;
    }

    // ------------------------------------------------------------------ Refine with AI panel

    private openPanel(target: string) {
        const host = this.host(target);
        if (!host) return;
        host.innerHTML = "";
        const panel = this.el("div", undefined, "border:1px solid #999;border-radius:6px;padding:12px;margin-top:12px;");
        panel.appendChild(this.el("h3", "Refine with AI"));
        const log = this.el("div", undefined, "max-height:300px;overflow:auto;margin-bottom:8px;");
        const options = this.el("div", undefined, "margin-bottom:8px;");
        const proposal = this.el("div");
        const input = document.createElement("textarea");
        input.setAttribute("rows", "3");
        input.setAttribute("maxlength", "2000");
        input.setAttribute("style", "width:100%;box-sizing:border-box;");
        input.setAttribute("placeholder", "Describe what you want in plain language");
        const row = this.el("p");
        const send = this.el("button", "Send") as HTMLButtonElement;
        send.type = "button";
        const cancel = this.el("button", "Cancel") as HTMLButtonElement;
        cancel.type = "button";
        row.appendChild(send);
        row.appendChild(document.createTextNode(" "));
        row.appendChild(cancel);
        const status = this.el("p", "", "font-style:italic;");
        panel.append(log, options, proposal, input, row, status);
        host.appendChild(panel);
        input.focus();

        let session = "";
        let hash = "";
        let busy = false;

        const addLine = (who: string, text: string) => {
            const p = this.el("p", undefined, "margin:4px 0;");
            const b = this.el("strong", who + ": ");
            p.appendChild(b);
            p.appendChild(document.createTextNode(text));
            log.appendChild(p);
            log.scrollTop = log.scrollHeight;
        };

        const submit = (text: string) => {
            text = text.trim();
            if (busy || !text) return;
            busy = true;
            send.disabled = true;
            status.textContent = "Thinking...";
            options.innerHTML = "";
            addLine("You", text);
            input.value = "";
            this.post("/account/ai/policy/refine", {target: target, session: session, message: text}, (d) => {
                busy = false;
                send.disabled = false;
                status.textContent = "";
                session = d.session;
                addLine("AI", d.message || "");
                if (d.status === "proposal" && d.proposed_markdown) {
                    hash = d.hash;
                    this.showProposal(proposal, d, () => this.accept(target, session, hash), () => {
                        input.focus();
                        status.textContent = "Tell the AI what to change, then Send.";
                    }, () => {
                        host.innerHTML = "";
                    });
                } else {
                    proposal.innerHTML = "";
                    (d.options || []).forEach((o: string) => {
                        const ob = this.el("button", o) as HTMLButtonElement;
                        ob.type = "button";
                        ob.setAttribute("style", "margin:0 6px 6px 0;");
                        ob.onclick = () => submit(o);
                        options.appendChild(ob);
                    });
                    input.focus();
                }
            }, () => {
                busy = false;
                send.disabled = false;
                status.textContent = "";
                input.value = text;
            });
        };
        send.onclick = () => submit(input.value);
        cancel.onclick = () => {
            host.innerHTML = "";
        };
    }

    private showProposal(box: HTMLElement, d: any, onAccept: () => void, onRefine: () => void, onCancel: () => void) {
        box.innerHTML = "";
        box.setAttribute("style", "border-top:1px solid #ccc;margin:8px 0;padding-top:8px;");
        box.appendChild(this.el("h4", "Proposed document"));
        box.appendChild(this.el("pre", d.proposed_markdown, "white-space:pre-wrap;background:#f6f6f6;color:#1a1a1a;border:1px solid #ccc;border-radius:4px;padding:8px;margin:8px 0 16px 0;"));
        box.appendChild(this.el("h4", "Changes from the current version"));
        box.appendChild(this.diffView(d.current_markdown || "", d.proposed_markdown));
        if (d.change_summary && d.change_summary.length) {
            box.appendChild(this.el("h4", "Summary"));
            const ul = this.el("ul");
            d.change_summary.forEach((s: string) => ul.appendChild(this.el("li", s)));
            box.appendChild(ul);
        }
        if (d.excluded && d.excluded.length) {
            box.appendChild(this.el("h4", "Not included"));
            const ul = this.el("ul");
            d.excluded.forEach((x: any) => ul.appendChild(this.el("li", x.request + " - " + x.reason)));
            box.appendChild(ul);
        }
        if (d.moved_elsewhere && d.moved_elsewhere.length) {
            box.appendChild(this.el("h4", "Belongs elsewhere"));
            const ul = this.el("ul");
            d.moved_elsewhere.forEach((x: any) => ul.appendChild(this.el("li", x.content + " - " + x.where)));
            box.appendChild(ul);
        }
        const row = this.el("p");
        const mk = (label: string, fn: () => void) => {
            const b = this.el("button", label) as HTMLButtonElement;
            b.type = "button";
            b.onclick = fn;
            row.appendChild(b);
            row.appendChild(document.createTextNode(" "));
        };
        mk("Accept", onAccept);
        mk("Refine", onRefine);
        mk("Cancel", onCancel);
        box.appendChild(row);
    }

    /** Simple line diff (longest common subsequence). Added lines green, removed red; escaping via textContent. */
    private diffView(oldText: string, newText: string): HTMLElement {
        const a = oldText.trim() ? oldText.trim().split("\n") : [];
        const b = newText.trim() ? newText.trim().split("\n") : [];
        const n = a.length, m = b.length;
        const lcs: number[][] = [];
        for (let i = 0; i <= n; i++) lcs.push(new Array(m + 1).fill(0));
        for (let i = n - 1; i >= 0; i--) {
            for (let j = m - 1; j >= 0; j--) {
                lcs[i][j] = a[i] === b[j] ? lcs[i + 1][j + 1] + 1 : Math.max(lcs[i + 1][j], lcs[i][j + 1]);
            }
        }
        const out = this.el("div", undefined, "font-family:monospace;white-space:pre-wrap;background:#ffffff;color:#1a1a1a;border:1px solid #ccc;border-radius:4px;padding:6px;margin:8px 0 16px 0;");
        const line = (prefix: string, text: string, style: string) => {
            out.appendChild(this.el("div", prefix + text, style));
        };
        let i = 0, j = 0;
        while (i < n || j < m) {
            if (i < n && j < m && a[i] === b[j]) {
                line("  ", a[i], "color:#444;");
                i++;
                j++;
            } else if (j < m && (i >= n || lcs[i][j + 1] >= lcs[i + 1][j])) {
                line("+ ", b[j], "background:#d4f5d4;color:#0b3d0b;");
                j++;
            } else {
                line("- ", a[i], "background:#fbdcdc;color:#5c0a0a;text-decoration:line-through;");
                i++;
            }
        }
        return out;
    }

    private accept(target: string, session: string, hash: string) {
        this.post("/account/ai/policy/accept", {target: target, session: session, hash: hash}, () => {
            window.location.reload();
        });
    }

    // ------------------------------------------------------------------ version history

    private openHistory(target: string) {
        const host = this.host(target);
        if (!host) return;
        this.post("/account/ai/policy/history", {target: target}, (rows: any) => {
            host.innerHTML = "";
            const box = this.el("div", undefined, "margin-top:12px;");
            box.appendChild(this.el("h3", "Version history"));
            if (!rows.length) {
                box.appendChild(this.el("p", "No versions yet."));
            } else {
                const table = this.el("table", undefined, "width:100%;border-collapse:collapse;");
                const head = this.el("tr");
                ["Version", "When", "Editor", "Change", ""].forEach((h) => head.appendChild(this.el("th", h, "text-align:left;padding:4px;")));
                table.appendChild(head);
                rows.forEach((r: any) => {
                    const tr = this.el("tr");
                    tr.appendChild(this.el("td", String(r.version) + (r.current ? " (current)" : ""), "padding:4px;"));
                    tr.appendChild(this.el("td", r.created, "padding:4px;"));
                    tr.appendChild(this.el("td", r.editor, "padding:4px;"));
                    const sum = (r.summary || []).join("; ") || (r.source === "revert" ? "Reverted to version " + r.reverted_from : "");
                    tr.appendChild(this.el("td", sum, "padding:4px;"));
                    const td = this.el("td", undefined, "padding:4px;");
                    if (!r.current) {
                        const b = this.el("button", "Revert") as HTMLButtonElement;
                        b.type = "button";
                        b.onclick = () => this.revert(target, r.version);
                        td.appendChild(b);
                    }
                    tr.appendChild(td);
                    table.appendChild(tr);
                });
                box.appendChild(table);
            }
            const close = this.el("button", "Close") as HTMLButtonElement;
            close.type = "button";
            close.onclick = () => {
                host.innerHTML = "";
            };
            box.appendChild(close);
            host.appendChild(box);
        });
    }

    /** Revert goes through the same hash-bound accept endpoint: prepare a server-side proposal, then accept its hash. */
    private async revert(target: string, version: number) {
        if (!(await peakConfirm("Restore version " + version + " as a new version?"))) return;
        this.post("/account/ai/policy/revert", {target: target, version: version}, (d) => {
            this.accept(target, d.session, d.hash);
        });
    }
}
